import { megabytes, type CompanionDownloadEvent } from "./companion-download.js";

/**
 * A quarter of the asset, or half a minute, whichever comes first.
 *
 * Both are needed, and neither alone would do. The asset is ~121 MB and a
 * measured link fetched it in 17.9 seconds, so a timer on its own would print
 * NOTHING on a fast connection — the step carries that case, at four lines
 * spaced about four seconds apart. On a slow link the reverse holds: a quarter
 * can be four minutes away, and the timer is what keeps a multi-minute download
 * from reading as a hang to the coding agent driving the install.
 */
const STEP_FRACTION = 0.25;
const INTERVAL_MS = 30_000;

export interface DownloadProgressLinesOptions {
  readonly write: (line: string) => void;
  /** Injected so the cadence can be tested without sleeping through it. */
  readonly now?: () => number;
}

/**
 * Render a download's events as plain lines, for an audience that is a program.
 *
 * The founder path is the paste runbook driven by a coding agent: the output is
 * captured, there is no terminal to redraw, and these lines are read twice —
 * once as a keepalive proving the download is alive, and once as the material
 * the agent relays ("80 of 121 MB..."). So they carry bytes, total and percent
 * in a shape that parses, and nothing is drawn in place.
 *
 * Throttling lives HERE rather than in the downloader, which emits every chunk.
 * A downloader that pre-throttled to quarters would hand the desktop installer
 * — the other consumer of these events — four points to draw a progress bar
 * from. It also keeps the cadence a pure function of an injectable clock.
 */
export function downloadProgressLines(
  options: DownloadProgressLinesOptions,
): (event: CompanionDownloadEvent) => void {
  const now = options.now ?? Date.now;
  let lastStep = 0;
  let lastReceived = 0;
  // Null until the first event: the interval is measured from when bytes
  // started arriving, and the reporter is built before the request is made.
  let lastEmitAt: number | null = null;

  const emit = (line: string): void => {
    lastEmitAt = now();
    options.write(line);
  };

  return (event: CompanionDownloadEvent): void => {
    if (event.kind === "retrying") {
      // One line per cause. A single line would have to be true of a body that
      // stopped part way, a request that reached nobody, and a server that
      // answered and refused — and no sentence is true of all three.
      emit(
        event.cause === "not-connected"
          ? "  Couldn't connect — retrying.\n"
          : event.cause === "server-error"
            ? "  The download service answered with an error — retrying.\n"
            : `  The connection dropped at ${megabytes(event.received)} — retrying.\n`,
      );
      return;
    }
    if (event.kind === "resumed") {
      // The quarters already on disk are behind us; announcing them now would
      // report progress this run did not make.
      if (event.total !== null && event.total > 0) {
        lastStep = Math.floor(event.received / event.total / STEP_FRACTION);
      }
      lastReceived = event.received;
      emit(`  Resuming an interrupted download from ${megabytes(event.received)}.\n`);
      return;
    }
    // The interval is measured from the first byte, not from when this reporter
    // was built — but the event that starts the clock is still judged on its
    // merits. Swallowing it outright would drop a genuine milestone on any
    // download whose first chunk is already a large share of a small asset.
    lastEmitAt ??= now();
    // A download does not only go forwards. When the server ignores a Range, or
    // answers 416, the transfer starts over from zero — and a reporter that
    // assumed the count only rises would then stay silent all the way back up
    // to where it had been, while the founder watches the number fall with no
    // line saying why.
    if (event.received < lastReceived) lastStep = 0;
    lastReceived = event.received;
    const total = event.total !== null && event.total > 0 ? event.total : null;
    if (total === null) {
      // No declared length, so a percentage would be invented. GitHub's asset
      // CDN always declares one; this is the degenerate path.
      if (now() - lastEmitAt < INTERVAL_MS) return;
      emit(`  ${megabytes(event.received)} downloaded.\n`);
      return;
    }
    const fraction = event.received / total;
    // Completion is the caller's own outcome line, one line later. A 100% line
    // between them says the same thing twice.
    if (fraction >= 1) return;
    const step = Math.floor(fraction / STEP_FRACTION);
    if (step <= lastStep && now() - lastEmitAt < INTERVAL_MS) return;
    lastStep = step;
    emit(
      `  ${Math.floor(fraction * 100).toString()}% · ${megabytes(event.received)} of ${megabytes(total)}\n`,
    );
  };
}
